import {Node} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ProcessSchemaMapperType, ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec, LookupStepSpec, LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {
  ProcessFlowchartSchemaFieldMapperConfigSpec,
  ProcessFlowchartSchemaMapperConfigSpec,
} from 'processes/dao/ProcessSchemaDAO';
import {
  TaskSchemaNodeData,
  TaskSchemaStaticNodeData,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';

import {ProcessTaskSchemaGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {getSchemaFieldInfoFromHandle, getTaskSchemaIdFromNodeIdentifier} from './flowchartIdentifierUtils';
import {
  ProcessFlowchartEdgeType,
  TaskSchemaNodeSection,
  TransitionSource,
  FlowchartStaticEdgeData,
  CustomMappingData,
} from './transitionModalWithFlowchartEditorConstants';

interface BuildSchemaMapperConfigFromTransitionDataRunnerArgs {
  destinationNode: TaskSchemaStaticNodeData;
  edges: Array<FlowchartStaticEdgeData>;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  transitionSource: TransitionSource;
  allTaskSchemasById: Record<string, ProcessTaskSchemaGraphQL>;
}

class BuildSchemaMapperConfigFromTransitionDataRunner {
  private readonly destinationNode: TaskSchemaStaticNodeData;
  private readonly edges: Array<FlowchartStaticEdgeData>;
  private readonly transitionSource: TransitionSource;
  private readonly allTaskSchemasById: Record<string, ProcessTaskSchemaGraphQL>;
  private readonly customMappingDataByEdgeId: Record<string, CustomMappingData>;

  constructor(args: BuildSchemaMapperConfigFromTransitionDataRunnerArgs) {
    this.destinationNode = args.destinationNode;
    this.edges = args.edges;
    this.transitionSource = args.transitionSource;
    this.allTaskSchemasById = args.allTaskSchemasById;
    this.customMappingDataByEdgeId = args.customMappingDataByEdgeId;
  }

  run(): ProcessFlowchartSchemaMapperConfigSpec {
    const clientId = uuidv4();
    const toTaskSchemaId = getTaskSchemaIdFromNodeIdentifier(this.destinationNode.id);
    const toSchemaId =
      this.allTaskSchemasById[toTaskSchemaId].executionType === ProcessExecutionType.FLOWCHART
        ? this.allTaskSchemasById[toTaskSchemaId].outputSchema!.id
        : toTaskSchemaId;

    return {
      clientId,
      toSchemaId,
      schemaFieldMapperConfigs: this.buildSchemaFieldMapperConfigs(),
      mapperType: ProcessSchemaMapperType.FIELD_MAPPER,
    };
  }

  buildFieldMapperConfigFromDataFlowEdge(
    dataFlowEdge: FlowchartStaticEdgeData
  ): ProcessFlowchartSchemaFieldMapperConfigSpec {
    const fromTaskSchemaId = getTaskSchemaIdFromNodeIdentifier(dataFlowEdge.source);
    const fromSchemaId =
      this.transitionSource === TransitionSource.OUTPUTS
        ? this.allTaskSchemasById[fromTaskSchemaId].outputSchema!.id
        : fromTaskSchemaId;

    const fromFieldHandle = dataFlowEdge.sourceHandle;
    const toFieldHandle = dataFlowEdge.targetHandle;
    assert(fromFieldHandle != null, `fromFieldHandle is null for edge ${dataFlowEdge.id}`);
    assert(toFieldHandle != null, `toFieldHandle is null for edge ${dataFlowEdge.id}`);
    const fromFieldInfo = getSchemaFieldInfoFromHandle(fromFieldHandle);
    const {fieldId: toFieldId} = getSchemaFieldInfoFromHandle(toFieldHandle);
    const customMappingData = this.customMappingDataByEdgeId[dataFlowEdge.id];
    const fieldMapperConfig: ProcessFlowchartSchemaFieldMapperConfigSpec = {
      fromSchemaId,
      resultMustBeNonNullToMap: customMappingData?.resultMustBeNonNullToMap ?? false,
      toSchemaFieldId: toFieldId,
      lookupConfig:
        customMappingData?.lookupConfig != null
          ? customMappingData.lookupConfig
          : this.getLookupConfigFromFieldAndTransitionSource(fromFieldInfo),
    };

    return fieldMapperConfig;
  }

  private buildSchemaFieldMapperConfigs(): Array<ProcessFlowchartSchemaFieldMapperConfigSpec> {
    const dataFlowEdges = this.edges.filter((edge) => edge.type === ProcessFlowchartEdgeType.DATA_FLOW);
    return dataFlowEdges.map((edge) => this.buildFieldMapperConfigFromDataFlowEdge(edge));
  }

  private getLookupConfigFromFieldAndTransitionSource(fieldInfo: {
    fieldId: string;
    taskSchemaNodeSection: TaskSchemaNodeSection;
  }): LookupConfigSpec {
    const lookupSteps: Array<LookupStepSpec> = [];
    // If the transition source is the node output but the field is coming from the node input, we need to add
    // a lookup step to go from the output to the inputs (aka from the process output to the process task).
    if (
      fieldInfo.taskSchemaNodeSection === TaskSchemaNodeSection.INPUTS &&
      this.transitionSource === TransitionSource.OUTPUTS
    ) {
      lookupSteps.push({type: LookupStepType.PROCESS_TASK});
    }
    lookupSteps.push({type: LookupStepType.SCHEMA_FIELD, schemaField: fieldInfo.fieldId});
    return {
      isMulti: false,
      lookupSteps,
    };
  }
}

/**
 * Builds the default field mapper config for a data flow edge.
 *
 * This is used to differentiate between a "field-to-field" mapping configured by drawing an edge between two
 * fields and a "custom mapping" configured by a custom lookup config.
 */
export function buildDefaultSchemaFieldMapperConfigFromDataFlowEdge({
  dataFlowEdge,
  destinationNode,
  transitionSource,
  allTaskSchemasById,
}: {
  dataFlowEdge: FlowchartStaticEdgeData;
  destinationNode: Node<TaskSchemaNodeData>;
  transitionSource: TransitionSource;
  allTaskSchemasById: Record<string, ProcessTaskSchemaGraphQL>;
}): ProcessFlowchartSchemaFieldMapperConfigSpec {
  const runner = new BuildSchemaMapperConfigFromTransitionDataRunner({
    destinationNode: destinationNode.data,
    edges: [dataFlowEdge],
    customMappingDataByEdgeId: {},
    transitionSource,
    allTaskSchemasById,
  });
  return runner.buildFieldMapperConfigFromDataFlowEdge(dataFlowEdge);
}

export default function buildSchemaMapperConfigFromTransitionData(
  args: BuildSchemaMapperConfigFromTransitionDataRunnerArgs
): ProcessFlowchartSchemaMapperConfigSpec {
  const runner = new BuildSchemaMapperConfigFromTransitionDataRunner(args);
  return runner.run();
}
